import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPGym = () => {
  const { user } = useAuth();
  const [gymSessions, setGymSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: `${command} ${params.join(' ')}`.trim(),
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('MCP Server error:', error);
      throw error;
    }
  };

  // Load gym sessions from MCP server
  const loadGymSessions = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_gym');
      if (result.status === 'success') {
        setGymSessions(result.gym || []);
      } else {
        throw new Error(result.message || 'Failed to load gym sessions');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading gym sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add gym session via MCP server
  const addGymSession = async (gymData) => {
    if (!user) return null;
    
    try {
      const result = await callMCPServer('add_gym', [gymData.exercise]);
      if (result.status === 'success') {
        await loadGymSessions();
        return result.gym;
      } else {
        throw new Error(result.message || 'Failed to add gym session');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding gym session:', err);
      throw err;
    }
  };

  // Update gym session via MCP server
  const updateGymSession = async (id, updates) => {
    if (!user) return null;
    
    try {
      const updateString = Object.entries(updates)
        .map(([key, value]) => `${key}:${value}`)
        .join(' ');
      
      const result = await callMCPServer('update_gym', [id, updateString]);
      
      if (result.status === 'success') {
        await loadGymSessions();
        return result.gym;
      } else {
        throw new Error(result.message || 'Failed to update gym session');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating gym session:', err);
      throw err;
    }
  };

  // Delete gym session via MCP server
  const deleteGymSession = async (id) => {
    if (!user) return;
    
    try {
      const result = await callMCPServer('delete_gym', [id]);
      if (result.status === 'success') {
        await loadGymSessions();
      } else {
        throw new Error(result.message || 'Failed to delete gym session');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting gym session:', err);
      throw err;
    }
  };

  // Load gym sessions when user changes
  useEffect(() => {
    if (user) {
      loadGymSessions();
    } else {
      setGymSessions([]);
    }
  }, [user, loadGymSessions]);

  return {
    gymSessions,
    loading,
    error,
    addGymSession,
    updateGymSession,
    deleteGymSession,
    loadGymSessions,
    setError
  };
};
